import { Box, Button, Group, Stack, Tabs, Text } from '@mantine/core';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import CodeEditor from '@uiw/react-textarea-code-editor';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnsavedChangesWarning } from '../../../hooks/useUnsavedChangesWarning';
import { type FieldType, getFieldTypeMeta } from '../../../lib/form-field-types';
import {
  dumpFormSpecToToml,
  type FormField,
  type FormSpec,
  parseTomlToFormSpec,
} from '../../../lib/form-specs';
import { sanitizeFieldName } from '../../../lib/sanitize-field';
import { FieldCanvas } from './FieldCanvas';
import classes from './FieldEditor.module.css';
import { FieldEditorHeader, type FieldEditorViewMode } from './FieldEditorHeader';
import { FieldPalette } from './FieldPalette';
import {
  type FieldNameChange,
  type FieldPropertiesChange,
  FieldPropertiesPanel,
  type FieldSettingsChange,
} from './FieldPropertiesPanel';

export interface FieldEditorProps {
  formTitle: string;
  initialSpec: string | undefined;
  onSave: (spec: string) => Promise<void> | void;
  onCancel: () => void;
}

type MobilePane = 'palette' | 'canvas' | 'properties';

const EMPTY_SPEC: FormSpec = {
  fields: {},
  settings: { discard_additional_fields: false },
};

const sanitizeOnTypeChange = (field: FormField): FormField => {
  const meta = getFieldTypeMeta(field.field);
  const next: FormField = { ...field };
  if (!meta?.hasOptions && next.options) {
    delete next.options;
  }
  if (!meta?.hasPlaceholder && next.placeholder) {
    delete next.placeholder;
  }
  return next;
};

export function FieldEditor({ formTitle, initialSpec, onSave, onCancel }: FieldEditorProps) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [spec, setSpec] = useState<FormSpec>(() => {
    const parsed = initialSpec ? parseTomlToFormSpec(initialSpec) : undefined;
    return parsed ?? { ...EMPTY_SPEC, settings: { ...EMPTY_SPEC.settings } };
  });
  const [tomlString, setTomlString] = useState<string>(() => initialSpec || '');
  const [selectedKey, setSelectedKey] = useState<string | null>(() => {
    const parsed = initialSpec ? parseTomlToFormSpec(initialSpec) : undefined;
    if (!parsed?.fields) return null;
    const keys = Object.keys(parsed.fields);
    return keys.length > 0 ? keys[0] : null;
  });
  const [viewMode, setViewMode] = useState<FieldEditorViewMode>('visual');
  const [isSaving, setIsSaving] = useState(false);
  const [mobilePane, setMobilePane] = useState<MobilePane>('canvas');

  // Echo guard for the spec → TOML → spec round-trip
  const tomlSourceRef = useRef<string>(initialSpec || '');
  const isInternalChangeRef = useRef<boolean>(false);

  const [debouncedSpec] = useDebouncedValue(spec, 500);

  const initialSnapshotRef = useRef<string>(initialSpec || '');
  const isDirty = tomlString !== initialSnapshotRef.current;

  useUnsavedChangesWarning({
    when: isDirty && !isSaving,
    title: t('fieldEditor.unsavedTitle'),
    body: t('fieldEditor.unsavedBody'),
    confirmLabel: t('formEdit.unsavedChangesDiscard'),
    cancelLabel: t('formEdit.unsavedChangesStay'),
  });

  // spec → TOML (debounced)
  useEffect(() => {
    if (!isInternalChangeRef.current) return;
    const toml = dumpFormSpecToToml(debouncedSpec);
    tomlSourceRef.current = toml;
    setTomlString(toml);
  }, [debouncedSpec]);

  // TOML → spec (only when external)
  useEffect(() => {
    if (tomlString === tomlSourceRef.current) return;
    try {
      const parsed = parseTomlToFormSpec(tomlString);
      if (parsed) {
        isInternalChangeRef.current = false;
        setSpec(parsed);
      }
    } catch (e) {
      console.error('Failed to parse TOML:', e);
    }
  }, [tomlString]);

  const onChange = useCallback((change: FieldPropertiesChange) => {
    setSpec((current) => {
      const target = current.fields[change.fieldName];
      if (!target) return current;
      const next = { ...current, fields: { ...current.fields } };
      if (change.value !== undefined) {
        next.fields[change.fieldName] = {
          ...target,
          [change.fieldKey]: change.value,
          ...(change.fieldKey === 'required' && change.value === true
            ? { on_fail: 'reject' as const }
            : {}),
        };
      } else {
        const { [change.fieldKey]: _drop, ...rest } = target;
        next.fields[change.fieldName] = rest as FormField;
      }
      isInternalChangeRef.current = true;
      return next;
    });
  }, []);

  const onSettingsChange = useCallback((change: FieldSettingsChange) => {
    setSpec((current) => {
      if (current.settings[change.fieldKey] === change.value) return current;
      isInternalChangeRef.current = true;
      return {
        ...current,
        settings: { ...current.settings, [change.fieldKey]: change.value },
      };
    });
  }, []);

  const onFieldNameChange = useCallback((change: FieldNameChange) => {
    setSpec((current) => {
      if (!current.fields[change.oldFieldName]) return current;
      if (change.newFieldName !== change.oldFieldName && current.fields[change.newFieldName]) {
        console.warn('Field name already exists:', change.newFieldName);
        return current;
      }
      const updated: FormSpec['fields'] = {};
      Object.keys(current.fields).forEach((key) => {
        if (key === change.oldFieldName) {
          updated[change.newFieldName] = {
            ...current.fields[change.oldFieldName],
            name: change.newFieldName,
          };
        } else {
          updated[key] = current.fields[key];
        }
      });
      isInternalChangeRef.current = true;
      return { ...current, fields: updated };
    });
    setSelectedKey((curr) => (curr === change.oldFieldName ? change.newFieldName : curr));
  }, []);

  const handleAdd = useCallback(
    (type: FieldType) => {
      const meta = getFieldTypeMeta(type);
      const labelKey = meta?.labelKey ?? type;
      const label = t(labelKey);
      const baseName = sanitizeFieldName(label) || type;

      setSpec((current) => {
        let finalName = baseName;
        let counter = 1;
        while (current.fields[finalName]) {
          counter += 1;
          finalName = `${baseName}_${counter}`;
        }
        const newField: FormField = {
          name: finalName,
          label,
          field: type,
          on_fail: 'pass',
          ...(type === 'email' ? { is_email: true } : {}),
          ...(type === 'url' ? { is_url: true } : {}),
        };
        isInternalChangeRef.current = true;
        const next: FormSpec = {
          ...current,
          fields: { ...current.fields, [finalName]: newField },
        };
        setSelectedKey(finalName);
        if (isMobile) setMobilePane('properties');
        return next;
      });
    },
    [t, isMobile]
  );

  const handleReorder = useCallback((oldIndex: number, newIndex: number) => {
    setSpec((current) => {
      const keys = Object.keys(current.fields);
      const [moved] = keys.splice(oldIndex, 1);
      keys.splice(newIndex, 0, moved);
      const reordered: FormSpec['fields'] = {};
      keys.forEach((k) => {
        reordered[k] = current.fields[k];
      });
      isInternalChangeRef.current = true;
      return { ...current, fields: reordered };
    });
  }, []);

  const handleDuplicate = useCallback((key: string) => {
    setSpec((current) => {
      const source = current.fields[key];
      if (!source) return current;

      // Strip any trailing _N suffix to find the base
      const m = key.match(/^(.+?)(?:_(\d+))?$/);
      const baseName = m ? m[1] : key;
      const existing = Object.keys(current.fields);
      const used = new Set<number>();
      existing.forEach((k) => {
        if (k === baseName) used.add(1);
        const match = k.match(new RegExp(`^${baseName}_(\\d+)$`));
        if (match) used.add(parseInt(match[1], 10));
      });
      let suffix = 2;
      while (used.has(suffix)) suffix += 1;
      const newName = `${baseName}_${suffix}`;
      const newField: FormField = { ...source, name: newName };

      // Insert immediately after the source
      const keys = Object.keys(current.fields);
      const idx = keys.indexOf(key);
      const reordered: FormSpec['fields'] = {};
      keys.forEach((k, i) => {
        reordered[k] = current.fields[k];
        if (i === idx) reordered[newName] = newField;
      });

      isInternalChangeRef.current = true;
      setSelectedKey(newName);
      return { ...current, fields: reordered };
    });
  }, []);

  const handleDelete = useCallback((key: string) => {
    setSpec((current) => {
      if (!current.fields[key]) return current;
      const { [key]: _removed, ...rest } = current.fields;
      isInternalChangeRef.current = true;
      return { ...current, fields: rest };
    });
    setSelectedKey((curr) => (curr === key ? null : curr));
  }, []);

  const handleTomlChange = useCallback((value: string) => {
    setTomlString(value);
  }, []);

  // Sync the latest TOML to a sanitized form before save (in case the type-change
  // dropped fields that weren't yet flushed via debounced effect).
  const sanitizedToml = useMemo(() => {
    const sanitized: FormSpec = {
      ...spec,
      fields: Object.fromEntries(
        Object.entries(spec.fields).map(([k, f]) => [k, sanitizeOnTypeChange(f)])
      ),
    };
    return dumpFormSpecToToml(sanitized);
  }, [spec]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // If user is in TOML mode, prefer their hand-edited string verbatim.
      const out = viewMode === 'toml' ? tomlString : sanitizedToml;
      await onSave(out);
      initialSnapshotRef.current = out;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelect = (key: string) => {
    setSelectedKey(key);
    if (isMobile) setMobilePane('properties');
  };

  const showPalette = !isMobile || mobilePane === 'palette';
  const showCanvas = !isMobile || mobilePane === 'canvas';
  const showProperties = !isMobile || mobilePane === 'properties';

  return (
    <Box className={classes.shell}>
      <FieldEditorHeader
        formTitle={formTitle}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onBack={onCancel}
      />

      {viewMode === 'visual' ? (
        <Box className={classes.body}>
          <Box className={`${classes.palette} ${!showPalette ? classes.paneHidden : ''}`}>
            <FieldPalette onAdd={handleAdd} />
          </Box>
          <Box className={`${classes.canvas} ${!showCanvas ? classes.paneHidden : ''}`}>
            <FieldCanvas
              spec={spec}
              selectedKey={selectedKey}
              onSelect={handleSelect}
              onReorder={handleReorder}
              onDuplicate={handleDuplicate}
              onDelete={handleDelete}
            />
          </Box>
          <Box className={`${classes.properties} ${!showProperties ? classes.paneHidden : ''}`}>
            <FieldPropertiesPanel
              spec={spec}
              selectedKey={selectedKey}
              onChange={onChange}
              onFieldNameChange={onFieldNameChange}
              onSettingsChange={onSettingsChange}
              onShowToml={() => setViewMode('toml')}
            />
          </Box>
        </Box>
      ) : (
        <Box className={classes.tomlCanvas}>
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {t('fieldEditor.tomlHint')}
            </Text>
            <CodeEditor
              value={tomlString}
              language="toml"
              onChange={(evt) => handleTomlChange(evt.target.value)}
              padding={15}
              style={{
                fontFamily:
                  'ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace',
                minHeight: 400,
              }}
            />
          </Stack>
        </Box>
      )}

      {isMobile && viewMode === 'visual' && (
        <Box className={classes.mobileTabs}>
          <Tabs
            value={mobilePane}
            onChange={(v) => v && setMobilePane(v as MobilePane)}
            variant="pills"
          >
            <Tabs.List grow>
              <Tabs.Tab value="palette">{t('fieldEditor.paneFields')}</Tabs.Tab>
              <Tabs.Tab value="canvas">{t('fieldEditor.paneCanvas')}</Tabs.Tab>
              <Tabs.Tab value="properties">{t('fieldEditor.paneProperties')}</Tabs.Tab>
            </Tabs.List>
          </Tabs>
        </Box>
      )}

      <Box className={classes.actionBar}>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onCancel} disabled={isSaving}>
            {t('fieldEditor.cancel')}
          </Button>
          <Button onClick={handleSave} loading={isSaving} disabled={!isDirty && !isSaving}>
            {t('fieldEditor.save')}
          </Button>
        </Group>
      </Box>
    </Box>
  );
}
